import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  MessageSquare, 
  Search,
  Send,
  MoreVertical,
  Check,
  CheckCheck,
  ArrowRight,
  ArrowLeft,
  Loader,
  X,
  Reply
} from 'lucide-react';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import PermissionGate from '../components/PermissionGate';
import chatService from '../services/chatService';
import './ChatPage.css';

const ChatPage = () => {
  const toast = useToast();
  const { t, i18n } = useTranslation();
  const { user: currentUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isRTL = i18n.language === 'ar';
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedChat, setSelectedChat] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const messagesEndRef = useRef(null);

  // API state
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const attemptedConversationUserRef = useRef(null);

  // Reply state
  const [replyingTo, setReplyingTo] = useState(null);

  // Fetch conversations
  const fetchConversations = useCallback(async () => {
    setLoading(true);
    try {
      const response = await chatService.getConversations();
      if (response.success) {
        setConversations(response.data.conversations || response.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch conversations:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch messages for selected conversation
  const fetchMessages = useCallback(async (conversationId) => {
    setMessagesLoading(true);
    try {
      const response = await chatService.getMessages(conversationId);
      if (response.success) {
        setMessages(response.data.messages || response.data || []);
        await chatService.markAsRead(conversationId);
        setConversations(prev => prev.map(c => 
          (c._id || c.id) === conversationId ? { ...c, unreadCount: 0 } : c
        ));
      }
    } catch (err) {
      console.error('Failed to fetch messages:', err);
    } finally {
      setMessagesLoading(false);
    }
  }, []);

  // Check for user to chat with from navigation state
  useEffect(() => {
    const startConversationFromState = async () => {
      const targetUserId = location.state?.userId;
      if (!targetUserId) return;
      if (attemptedConversationUserRef.current === targetUserId) return;

      attemptedConversationUserRef.current = targetUserId;

      try {
        const response = await chatService.startConversation(targetUserId);
        if (response.success) {
          const conv = response.data.conversation;
          if (!conv.participant && location.state.userName) {
            conv.participant = { _id: targetUserId, name: location.state.userName };
          }
          setConversations(prev => {
            const exists = prev.find(c => (c._id || c.id) === (conv._id || conv.id));
            if (!exists) {
              return [conv, ...prev];
            }
            return prev;
          });
          setSelectedChat(conv);
          fetchMessages(conv._id || conv.id);
        } else {
          toast.error(response.message || 'فشل في بدء المحادثة');
        }
      } catch (err) {
        toast.error(err.message || 'فشل في بدء المحادثة');
      } finally {
        navigate(location.pathname, { replace: true, state: {} });
      }
    };

    startConversationFromState();
  }, [location.state, location.pathname, navigate, fetchMessages, toast]);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  useEffect(() => {
    if (selectedChat) {
      fetchMessages(selectedChat._id || selectedChat.id);
    }
  }, [selectedChat, fetchMessages]);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    if (messages.length > 0) {
      scrollToBottom();
    }
  }, [messages, scrollToBottom]);

  // Handle file selection
  // Send message (text only)
  const handleSendMessage = useCallback(async () => {
    if (!newMessage.trim() || !selectedChat || sending) return;

    const conversationId = selectedChat._id || selectedChat.id;
    setSending(true);
    
    try {
      const response = await chatService.sendMessage(conversationId, {
        text: newMessage.trim(),
        replyTo: replyingTo?._id || replyingTo?.id
      });
      
      if (response.success) {
        const newMsg = response.data.message || response.data;
        setMessages(prev => [...prev, newMsg]);
        
        setConversations(prev => prev.map(conv => {
          if ((conv._id || conv.id) === conversationId) {
            return {
              ...conv,
              lastMessageText: newMessage.trim(),
              lastMessageAt: new Date()
            };
          }
          return conv;
        }));
        
        setNewMessage('');
        setReplyingTo(null);
        setTimeout(scrollToBottom, 100);
      } else {
        toast.error(response.message || 'فشل في إرسال الرسالة');
      }
    } catch (err) {
      toast.error(err.message || 'فشل في إرسال الرسالة');
    } finally {
      setSending(false);
    }
  }, [newMessage, selectedChat, sending, replyingTo, scrollToBottom, toast]);


  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString('ar-SA', { hour: '2-digit', minute: '2-digit' });
  };

  const formatLastSeen = (date) => {
    if (!date) return '';
    const now = new Date();
    const diff = now - new Date(date);
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'الآن';
    if (minutes < 60) return `منذ ${minutes} دقيقة`;
    if (hours < 24) return `منذ ${hours} ساعة`;
    return `منذ ${days} يوم`;
  };

  const filteredConversations = conversations.filter(conv => {
    const participant = conv.participant || conv.otherParticipant || {};
    return (participant.name || '').toLowerCase().includes(searchQuery.toLowerCase()) || 
           (conv.lastMessageText || '').toLowerCase().includes(searchQuery.toLowerCase());
  });

  const getMessageStatus = (status) => {
    switch(status) {
      case 'sent': return <Check size={14} />;
      case 'delivered': return <CheckCheck size={14} />;
      case 'read': return <CheckCheck size={14} className="read" />;
      default: return null;
    }
  };

  const isMyMessage = (message) => {
    const senderId = message.sender?._id || message.sender?.id || message.sender;
    const myId = currentUser?._id || currentUser?.id;
    return senderId === myId || message.isMine;
  };

  const renderMessageContent = (message) => {
    return (
      <>
        {message.replyTo && (
          <div className="reply-reference">
            <Reply size={12} />
            <span>{message.replyTo.text || 'رسالة'}</span>
          </div>
        )}
        
        {message.text && <p>{message.text}</p>}
        
        <div className="message-meta">
          <span className="time">{formatTime(message.createdAt || message.time)}</span>
          {isMyMessage(message) && (
            <span className="status">{getMessageStatus(message.status)}</span>
          )}
        </div>
      </>
    );
  };

  if (loading) {
    return (
      <div className="chat-page">
        <div className="loading-container">
          <Loader className="spinner" size={48} />
          <p>جاري التحميل...</p>
        </div>
      </div>
    );
  }

  return (
    <PermissionGate feature="PAGE_CHAT" type="page">
    <div className={`chat-page ${selectedChat ? 'chat-open' : ''}`}>
      <div className="chat-sidebar">
        <div className="sidebar-header">
          <h2>
            <MessageSquare size={22} />
            {t('chat.title')}
          </h2>
        </div>
        
        <div className="search-box">
          <Search size={18} />
          <input 
            type="text"
            placeholder={t('common.search')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <div className="conversations-list">
          {filteredConversations.length === 0 ? (
            <div className="no-conversations">
              <MessageSquare size={40} />
              <p>لا توجد محادثات</p>
              <span>ابدأ محادثة من صفحة الشبكة</span>
            </div>
          ) : (
            filteredConversations.map(conv => {
              const convId = conv._id || conv.id;
              const participant = conv.participant || conv.otherParticipant || {};
              return (
                <div 
                  key={convId}
                  className={`conversation-item ${(selectedChat?._id || selectedChat?.id) === convId ? 'active' : ''}`}
                  onClick={() => setSelectedChat(conv)}
                >
                  <div className="user-avatar">
                    {participant.avatar ? (
                      <img src={participant.avatar} alt={participant.name} />
                    ) : (
                      (participant.name || 'U').charAt(0)
                    )}
                    {participant.online && <span className="online-dot"></span>}
                  </div>
                  <div className="conversation-info">
                    <div className="conversation-header">
                      <h4>{participant.name || 'غير معروف'}</h4>
                      <span className="time">{formatLastSeen(conv.lastMessageAt || conv.updatedAt)}</span>
                    </div>
                    <div className="conversation-preview">
                      <p>{conv.lastMessageText || 'لا توجد رسائل'}</p>
                      {conv.unreadCount > 0 && <span className="unread-badge">{conv.unreadCount}</span>}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      <div className="chat-main">
        {selectedChat ? (
          <>
            <div className="chat-header">
              <button className="back-btn" onClick={() => setSelectedChat(null)}>
                {isRTL ? <ArrowRight size={20} /> : <ArrowLeft size={20} />}
              </button>
              <div className="chat-user-info">
                <div className="user-avatar">
                  {(selectedChat.participant || selectedChat.otherParticipant || {}).avatar ? (
                    <img 
                      src={(selectedChat.participant || selectedChat.otherParticipant || {}).avatar} 
                      alt={(selectedChat.participant || selectedChat.otherParticipant || {}).name} 
                    />
                  ) : (
                    ((selectedChat.participant || selectedChat.otherParticipant || {}).name || 'U').charAt(0)
                  )}
                  {(selectedChat.participant || selectedChat.otherParticipant || {}).online && <span className="online-dot"></span>}
                </div>
                <div>
                  <h3>{(selectedChat.participant || selectedChat.otherParticipant || {}).name || 'غير معروف'}</h3>
                  <span className={`status ${(selectedChat.participant || selectedChat.otherParticipant || {}).online ? 'online' : ''}`}>
                    {(selectedChat.participant || selectedChat.otherParticipant || {}).online ? 'متصل الآن' : 'غير متصل'}
                  </span>
                </div>
              </div>
              <div className="chat-actions">
                <button className="action-btn">
                  <MoreVertical size={20} />
                </button>
              </div>
            </div>

            <div className="messages-container">
              {messagesLoading ? (
                <div className="loading-messages">
                  <Loader className="spinner" size={24} />
                  <p>جاري تحميل الرسائل...</p>
                </div>
              ) : messages.length === 0 ? (
                <div className="no-messages">
                  <MessageSquare size={48} />
                  <p>لا توجد رسائل بعد</p>
                  <span>ابدأ المحادثة الآن!</span>
                </div>
              ) : (
                messages.map(message => (
                  <div 
                    key={message._id || message.id}
                    className={`message ${isMyMessage(message) ? 'sent' : 'received'}`}
                  >
                    <div className="message-bubble">
                      {renderMessageContent(message)}
                    </div>
                    <button 
                      className="reply-btn"
                      onClick={() => setReplyingTo(message)}
                      title="رد"
                    >
                      <Reply size={14} />
                    </button>
                  </div>
                ))
              )}
              <div ref={messagesEndRef} />
            </div>

            {replyingTo && (
              <div className="reply-indicator">
                <div className="reply-content">
                  <Reply size={16} />
                  <span>الرد على: {replyingTo.text || 'رسالة'}</span>
                </div>
                <button onClick={() => setReplyingTo(null)}>
                  <X size={18} />
                </button>
              </div>
            )}

            <div className="chat-input">
              <input 
                type="text"
                placeholder="اكتب رسالتك..."
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={sending}
              />
              <button 
                className="send-btn"
                onClick={handleSendMessage}
                disabled={!newMessage.trim() || sending}
              >
                {sending ? <Loader className="spinner" size={20} /> : <Send size={20} />}
              </button>
            </div>
          </>
        ) : (
          <div className="no-chat-selected">
            <MessageSquare size={64} />
            <h3>{t('chat.selectConversation')}</h3>
            <p>اختر محادثة من القائمة أو ابدأ محادثة جديدة من صفحة الشبكة</p>
          </div>
        )}
      </div>
    </div>
    </PermissionGate>
  );
};

export default ChatPage;
